import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import toast from 'react-hot-toast';
import { 
  BookOpen, 
  Target, 
  Palette,
  Zap,
  Search,
  Users,
  Star,
  Clock,
  ArrowRight,
  ArrowLeft,
  PlayCircle,
  FileText,
  Download,
  Settings,
  TrendingUp,
  Lightbulb,
  CheckCircle,
  AlertTriangle,
  HelpCircle,
  Video,
  Book,
  User,
  Mail,
  Send,
  Phone,
  MessageSquare,
  Bug,
  CreditCard,
  Wrench
} from 'lucide-react';

// Custom styles for help content
const helpContentStyles = `
  .help-content-display h3 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 1rem;
    margin-top: 2rem;
  }
  .help-content-display h4 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #ffffff;
    margin-bottom: 0.75rem;
    margin-top: 1.5rem;
  }
  .help-content-display h5 {
    font-size: 1.1rem;
    font-weight: 600;
    color: #e5e7eb;
    margin-bottom: 0.5rem;
    margin-top: 1rem;
  }
  .help-content-display h6 {
    font-size: 1rem;
    font-weight: 600;
    color: #e5e7eb;
    margin-bottom: 0.5rem;
  }
  .help-content-display p {
    color: #d1d5db;
    margin-bottom: 1rem;
    line-height: 1.7;
  }
  .help-content-display ul, .help-content-display ol {
    margin-bottom: 1rem;
    padding-left: 1.5rem;
  }
  .help-content-display li {
    color: #d1d5db;
    margin-bottom: 0.5rem;
  }
  .help-content-display strong {
    font-weight: 600;
    color: #ffffff;
  }
  .help-content-display em {
    font-style: italic;
    color: #a1a1aa;
  }
  .help-content-display .bg-blue-500\/20 {
    background-color: rgb(59 130 246 / 0.2);
    border: 1px solid rgb(59 130 246 / 0.3);
    border-radius: 0.5rem;
    padding: 1rem;
    margin-bottom: 1rem;
  }
  .help-content-display .bg-green-500\/20 {
    background-color: rgb(34 197 94 / 0.2);
    border: 1px solid rgb(34 197 94 / 0.3);
    border-radius: 0.5rem;
    padding: 1rem;
    margin-bottom: 1rem;
  }
  .help-content-display .bg-red-500\/20 {
    background-color: rgb(239 68 68 / 0.2);
    border: 1px solid rgb(239 68 68 / 0.3);
    border-radius: 0.5rem;
    padding: 1rem;
    margin-bottom: 1rem;
  }
  .help-content-display .bg-yellow-500\/20 {
    background-color: rgb(234 179 8 / 0.2);
    border: 1px solid rgb(234 179 8 / 0.3);
    border-radius: 0.5rem;
    padding: 1rem;
    margin-bottom: 1rem;
  }
  .help-content-display .bg-purple-500\/20 {
    background-color: rgb(168 85 247 / 0.2);
    border: 1px solid rgb(168 85 247 / 0.3);
    border-radius: 0.5rem;
    padding: 1rem;
    margin-bottom: 1rem;
  }
  .help-content-display .bg-gray-800\/50 {
    background-color: rgb(31 41 55 / 0.5);
    border-radius: 0.375rem;
    padding: 1rem;
    margin-bottom: 1rem;
  }
  .help-content-display pre {
    background-color: rgb(17 24 39);
    border-radius: 0.375rem;
    padding: 1rem;
    overflow-x: auto;
    font-family: ui-monospace, SFMono-Regular, Consolas, monospace;
    font-size: 0.875rem;
    color: #e5e7eb;
    white-space: pre-wrap;
  }
  .help-content-display table {
    width: 100%;
    border-collapse: collapse;
    margin: 1rem 0;
  }
  .help-content-display td {
    padding: 0.5rem;
    border-bottom: 1px solid rgb(75 85 99);
    color: #d1d5db;
  }
`;

const HelpCenterPage = () => {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTutorial, setSelectedTutorial] = useState(null);
  
  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    category: '',
    subject: '',
    message: '',
    priority: 'medium'
  });
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  
  // Auto-populate user information when component mounts
  useEffect(() => {
    if (user) {
      setContactForm(prev => ({
        ...prev,
        name: user.full_name || '',
        email: user.email || ''
      }));
    }
  }, [user]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingContact(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/support/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          ...contactForm,
          user_context: {
            user_id: user?._id,
            subscription_status: user?.subscription_status || 'trial',
            credits: user?.credits || 0,
            is_premium: user?.is_premium || false,
            books_generated: user?.books_generated || 0,
            created_at: user?.created_at
          }
        })
      });

      if (response.ok) {
        toast.success('Support request sent successfully! We\'ll get back to you soon.');
        setContactForm({
          name: user?.full_name || '',
          email: user?.email || '',
          category: '',
          subject: '',
          message: '',
          priority: 'medium'
        });
      } else {
        throw new Error('Failed to send support request');
      }
    } catch (error) {
      console.error('Error sending support request:', error);
      toast.error('Failed to send support request. Please try again.');
    } finally {
      setIsSubmittingContact(false);
    }
  };

  const handleContactInputChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    setSelectedTutorial(null); // Reset tutorial selection when changing sections
    // Scroll to main content area when section changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Inject custom styles
    const styleSheet = document.createElement("style");
    styleSheet.innerText = helpContentStyles;
    document.head.appendChild(styleSheet);
    
    return () => {
      // Cleanup styles on unmount
      document.head.removeChild(styleSheet);
    };
  }, []);

  const sections = [
    {
      id: 'overview',
      title: 'Getting Started',
      icon: <BookOpen className="h-5 w-5" />,
      description: 'Quick start guide and onboarding'
    },
    {
      id: 'tutorials',
      title: 'Interactive Tutorials',
      icon: <PlayCircle className="h-5 w-5" />,
      description: 'Step-by-step walkthroughs'
    },
    {
      id: 'advanced',
      title: 'Advanced Strategies',
      icon: <TrendingUp className="h-5 w-5" />,
      description: 'Pro tips for power users'
    },
    {
      id: 'workflows',
      title: 'Automation Workflows',
      icon: <Zap className="h-5 w-5" />,
      description: 'Streamline your publishing'
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: <HelpCircle className="h-5 w-5" />,
      description: 'Solutions to common issues'
    },
    {
      id: 'contact',
      title: 'Contact Support',
      icon: <MessageSquare className="h-5 w-5" />,
      description: 'Get help from our team'
    }
  ];

  const tutorialContent = {
    overview: [
      {
        title: 'Quick Start Checklist',
        description: 'Your first 24 hours with AI BookMoney - complete setup guide',
        type: 'checklist',
        duration: '5 min',
        level: 'Beginner',
        content: `
          <h3>✅ Day 1: Complete Setup (30 minutes)</h3>
          
          <h4>Account Setup</h4>
          <ul>
            <li>✅ Verify your email and complete profile</li>
            <li>✅ Understand your 40 free credits (enough for 2-4 books)</li>
            <li>✅ Bookmark your Dashboard for quick access</li>
          </ul>
          
          <h4>Create Your First Book (20 minutes)</h4>
          <ol>
            <li><strong>Pick Your Niche:</strong> Start with what you know - business, health, relationships, or hobbies</li>
            <li><strong>Use Book Creation Wizard:</strong> Go to Dashboard → "Create New Book"</li>
            <li><strong>Title Formula:</strong> "How to [ACHIEVE RESULT] in [TIME FRAME] for [TARGET AUDIENCE]"</li>
            <li><strong>Keep It Simple:</strong> 15-25 pages for your first book</li>
          </ol>
          
          <h4>Generate Your Cover (5 minutes)</h4>
          <ul>
            <li>Use "KDP Mode" for proper dimensions (6x9 inches)</li>
            <li>Choose colors that match your genre (dark for thriller, bright for self-help)</li>
            <li>Ensure title is readable as thumbnail</li>
          </ul>
          
          <h4>Optimize Metadata (5 minutes)</h4>
          <ul>
            <li>Use our Metadata Optimizer tool</li>
            <li>Focus on 3-5 main keywords</li>
            <li>Write benefit-focused descriptions</li>
          </ul>
          
          <div class="bg-green-500/20 border border-green-500/30 rounded-lg p-4 mt-4">
            <h4>Success Metric</h4>
            <p>By end of day 1, you should have: 1 complete book manuscript, 1 professional cover, and optimized metadata ready for KDP upload.</p>
          </div>
        `
      },
      {
        title: 'Credit Optimization Guide',
        description: 'Maximize your ROI and get 10x more value from every credit',
        type: 'guide',
        duration: '8 min',
        level: 'Intermediate',
        content: `
          <h3>💰 Credit Economics: Every Credit Counts</h3>
          
          <h4>Credit Values (What costs what)</h4>
          <ul>
            <li><strong>Book Generation:</strong> 8-12 credits (depending on length)</li>
            <li><strong>Cover Creation:</strong> 3-5 credits per cover</li>
            <li><strong>Metadata Optimization:</strong> 2-3 credits</li>
            <li><strong>Regeneration:</strong> Same cost (plan accordingly!)</li>
          </ul>
          
          <h4>The 40-Credit Strategy (Free Trial)</h4>
          <p>Your free 40 credits can create:</p>
          <ul>
            <li><strong>Option A:</strong> 2 complete premium books (20 credits each)</li>
            <li><strong>Option B:</strong> 4 shorter books (10 credits each)</li>
            <li><strong>Option C:</strong> 3 books + extra covers for testing (13 credits per book + 1 extra cover)</li>
          </ul>
          
          <h4>Advanced Credit Hacks</h4>
          <ol>
            <li><strong>Batch Your Work:</strong> Create all content in one session to maintain context</li>
            <li><strong>Perfect Your Prompts:</strong> Spend time crafting detailed prompts to avoid regeneration</li>
            <li><strong>Cover Variations:</strong> Generate 2-3 cover options, test which converts better</li>
            <li><strong>Series Planning:</strong> Use metadata tool once for entire series (similar keywords)</li>
          </ol>
          
          <h4>📊 ROI Calculation</h4>
          <div class="bg-blue-500/20 border border-blue-500/30 rounded-lg p-4">
            <p><strong>Average Book Revenue:</strong> $50-200/month per book</p>
            <p><strong>Credit Cost:</strong> ~$1.50 per book (after trial)</p>
            <p><strong>ROI:</strong> 3,300% - 13,300% annually</p>
            <p><em>Even conservative $25/month per book = 1,600% ROI</em></p>
          </div>
        `
      },
      {
        title: 'Common Mistakes & Quick Fixes',
        description: 'Avoid the 7 pitfalls that kill 90% of books before they launch',
        type: 'article',
        duration: '6 min',
        level: 'Beginner',
        content: `
          <h3>🚫 The 7 Deadly Mistakes (And How to Fix Them)</h3>
          
          <h4>Mistake #1: Generic Titles</h4>
          <p><strong>❌ Bad:</strong> "Digital Marketing Guide"</p>
          <p><strong>✅ Good:</strong> "Digital Marketing for Local Businesses: Get 100+ Customers in 90 Days Without Paid Ads"</p>
          <p><strong>Fix:</strong> Include specific outcome + timeframe + target audience</p>
          
          <h4>Mistake #2: Boring Covers</h4>
          <p><strong>❌ Bad:</strong> Text-only covers with default fonts</p>
          <p><strong>✅ Good:</strong> High-contrast, genre-appropriate imagery with bold, readable fonts</p>
          <p><strong>Fix:</strong> Use our Cover Generator's psychology-based templates</p>
          
          <h4>Mistake #3: Weak Descriptions</h4>
          <p><strong>❌ Bad:</strong> "This book teaches you about..."</p>
          <p><strong>✅ Good:</strong> "Struggling with low sales? This proven system helped 500+ businesses increase revenue 40% in 90 days..."</p>
          <p><strong>Fix:</strong> Start with reader's pain point, then promise transformation</p>
          
          <h4>Mistake #4: Wrong Categories</h4>
          <p><strong>❌ Bad:</strong> Choosing competitive categories</p>
          <p><strong>✅ Good:</strong> Finding categories where you can rank in top 10</p>
          <p><strong>Fix:</strong> Research category bestsellers - if they have 10,000+ reviews, too competitive</p>
          
          <h4>Mistake #5: Poor Keywords</h4>
          <p><strong>❌ Bad:</strong> "business, success, money"</p>
          <p><strong>✅ Good:</strong> "small business marketing, local SEO, customer acquisition"</p>
          <p><strong>Fix:</strong> Use our Metadata Optimizer for data-driven keyword research</p>
          
          <h4>Mistake #6: No Series Planning</h4>
          <p><strong>❌ Bad:</strong> One-off books</p>
          <p><strong>✅ Good:</strong> 3-5 book series with cross-promotion</p>
          <p><strong>Fix:</strong> Plan Book 2 & 3 before publishing Book 1</p>
          
          <h4>Mistake #7: Ignoring Format Quality</h4>
          <p><strong>❌ Bad:</strong> Uploading without checking formatting</p>
          <p><strong>✅ Good:</strong> Testing all formats (PDF, EPUB, MOBI) before upload</p>
          <p><strong>Fix:</strong> Use our Book Manager's "Download All Formats" feature</p>
          
          <div class="bg-red-500/20 border border-red-500/30 rounded-lg p-4 mt-4">
            <h4>Pro Tip</h4>
            <p>Fix just 3 of these mistakes and you'll outperform 80% of books on Amazon. Fix all 7? You're in the top 5%.</p>
          </div>
        `
      }
    ],
    tutorials: [
      {
        title: 'AI Prompt Engineering for Better Books',
        description: 'Advanced prompting techniques that generate higher-quality, engaging content',
        type: 'guide',
        duration: '12 min',
        level: 'Advanced',
        content: `
          <h3>Mastering AI Prompts: The $1000/Hour Skill</h3>
          
          <h4>The POWER Framework</h4>
          <ul>
            <li><strong>P - Purpose:</strong> Define exactly what you want</li>
            <li><strong>O - Output:</strong> Specify format and structure</li>
            <li><strong>W - Who:</strong> Define your target audience</li>
            <li><strong>E - Examples:</strong> Provide context and style</li>
            <li><strong>R - Refinement:</strong> Add constraints and quality markers</li>
          </ul>
          
          <h4>High-Converting Prompt Templates</h4>
          
          <h5>For Non-Fiction Books:</h5>
          <div class="bg-gray-800/50 rounded p-4 mb-4">
            <pre>"Write a comprehensive chapter about [TOPIC] for [TARGET AUDIENCE]. 
            
Purpose: Help readers [SPECIFIC OUTCOME] in [TIMEFRAME]
Structure: Start with a hook/story, provide 3-5 actionable strategies, include real examples, end with implementation steps
Tone: [Conversational/Professional/Authoritative]
Length: 2000-2500 words
Audience Pain Points: [LIST 2-3 MAIN STRUGGLES]

Include:
- Subheadings every 300-400 words
- Bullet points for key concepts  
- Action steps marked with numbers
- One real-world example per strategy
- A 'Quick Win' tip they can implement immediately"</pre>
          </div>
          
          <h5>For Fiction Books:</h5>
          <div class="bg-gray-800/50 rounded p-4 mb-4">
            <pre>"Write Chapter [X] of a [GENRE] novel for [TARGET DEMOGRAPHIC].

Setting: [TIME PERIOD/LOCATION]  
Main Character: [NAME, AGE, KEY TRAIT]
Conflict: [MAIN TENSION/PROBLEM]
Chapter Goal: [WHAT NEEDS TO HAPPEN]
Tone: [Dark/Light/Suspenseful/Romantic]

Previous Chapter Summary: [2-3 sentences]
This Chapter Should:
- Advance the plot toward [SPECIFIC GOAL]
- Develop [CHARACTER]'s relationship with [OTHER CHARACTER]
- Include dialogue that reveals [CHARACTER TRAIT/PLOT POINT]
- End with [CLIFFHANGER/RESOLUTION/TRANSITION]

Length: 2500-3000 words
Show don't tell. Use sensory details. Vary sentence length."</pre>
          </div>
          
          <h4>Advanced Techniques</h4>
          
          <h5>1. Context Stacking</h5>
          <p>Build context across multiple prompts:</p>
          <ol>
            <li><strong>First Prompt:</strong> Establish character/topic foundation</li>
            <li><strong>Second Prompt:</strong> "Based on the foundation above, now..."</li>
            <li><strong>Third Prompt:</strong> "Continuing the style and tone..."</li>
          </ol>
          
          <h5>2. Quality Anchors</h5>
          <p>Reference high-quality examples:</p>
          <ul>
            <li>"Write in the style of Malcolm Gladwell's accessibility but with Seth Godin's brevity"</li>
            <li>"Match the pacing of a John Grisham thriller but with the character depth of Gillian Flynn"</li>
          </ul>
          
          <h5>3. Constraint-Based Improvement</h5>
          <ul>
            <li>"Use no more than 3 adjectives per paragraph"</li>
            <li>"Every paragraph must advance the plot"</li>
            <li>"Include one unexpected insight every 500 words"</li>
          </ul>
          
          <div class="bg-purple-500/20 border border-purple-500/30 rounded-lg p-4 mt-4">
            <h4>Success Metric</h4>
            <p>Good prompts eliminate 90% of editing. If you're spending hours editing AI content, improve your prompts first.</p>
          </div>
        `
      },
      {
        title: 'Cover Psychology & Conversion Optimization',
        description: 'Design principles and color psychology that convert browsers to buyers',
        type: 'guide',
        duration: '15 min',
        level: 'Intermediate',
        content: `
          <h3>🧠 Cover Psychology: The 3-Second Decision</h3>
          <p>Your cover has 3 seconds to convert a browser into a buyer. Here's the science:</p>
          
          <h4>Color Psychology by Genre</h4>
          
          <h5>Business/Self-Help:</h5>
          <ul>
            <li><strong>Blue:</strong> Trust, professionalism, stability (use for financial advice)</li>
            <li><strong>Orange:</strong> Energy, optimism, action (use for productivity/motivation)</li>
            <li><strong>Green:</strong> Growth, money, health (use for wealth/wellness)</li>
            <li><strong>Red:</strong> Urgency, power, attention (use sparingly for impact)</li>
          </ul>
          
          <h5>Fiction:</h5>
          <ul>
            <li><strong>Dark/Black:</strong> Mystery, thriller, suspense</li>
            <li><strong>Pastels:</strong> Romance, women's fiction</li>
            <li><strong>Bright Colors:</strong> Young adult, adventure</li>
            <li><strong>Muted Earth Tones:</strong> Literary fiction, historical</li>
          </ul>
          
          <h4>📐 The Golden Ratio Rules</h4>
          
          <h5>Typography Hierarchy (from top to bottom):</h5>
          <ol>
            <li><strong>Author Name:</strong> 15-20% of cover height</li>
            <li><strong>Title:</strong> 25-35% of cover height  </li>
            <li><strong>Subtitle:</strong> 8-12% of cover height</li>
            <li><strong>Supporting Elements:</strong> Fill remaining space</li>
          </ol>
          
          <h5>Contrast Formula:</h5>
          <ul>
            <li><strong>Background:</strong> Dark = Light text, Light = Dark text</li>
            <li><strong>Readability Test:</strong> Shrink cover to thumbnail size - can you read the title?</li>
            <li><strong>Color Temperature:</strong> Warm backgrounds + cool text (or vice versa)</li>
          </ul>
          
          <h4>🔥 High-Converting Cover Formulas</h4>
          
          <h5>The Authority Formula:</h5>
          <div class="bg-blue-500/20 border border-blue-500/30 rounded-lg p-4 mb-4">
            <p><strong>Best for:</strong> Business, self-help, instructional</p>
            <p><strong>Elements:</strong> Clean typography, minimal imagery, professional colors (blue, gray, white)</p>
            <p><strong>Message:</strong> "This author knows what they're talking about"</p>
          </div>
          
          <h5>The Transformation Formula:</h5>
          <div class="bg-green-500/20 border border-green-500/30 rounded-lg p-4 mb-4">
            <p><strong>Best for:</strong> Health, fitness, personal development</p>
            <p><strong>Elements:</strong> Before/after imagery, upward arrows, bright colors</p>
            <p><strong>Message:</strong> "This book will change your life"</p>
          </div>
          
          <h5>The Mystery Formula:</h5>
          <div class="bg-purple-500/20 border border-purple-500/30 rounded-lg p-4 mb-4">
            <p><strong>Best for:</strong> Fiction, thriller, mystery</p>
            <p><strong>Elements:</strong> Shadowy imagery, selective focus, dramatic lighting</p>
            <p><strong>Message:</strong> "You need to know what happens next"</p>
          </div>
          
          <h4>📊 A/B Testing Your Covers</h4>
          
          <h5>What to Test:</h5>
          <ol>
            <li><strong>Color Schemes:</strong> Warm vs Cool palettes</li>
            <li><strong>Typography:</strong> Bold vs Script fonts</li>
            <li><strong>Imagery:</strong> People vs Objects vs Abstract</li>
            <li><strong>Layout:</strong> Center vs Left-aligned text</li>
          </ol>
          
          <h5>Testing Strategy:</h5>
          <ul>
            <li><strong>Generate 3 variations</strong> using our Cover Generator</li>
            <li><strong>Test on social media</strong> (post as polls)</li>
            <li><strong>Check competitor analysis</strong> (what's working in your category?)</li>
            <li><strong>Monitor conversion rates</strong> for 30 days post-launch</li>
          </ul>
          
          <div class="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4 mt-4">
            <h4>Quick Win</h4>
            <p>Generate 2 covers: one that fits your genre perfectly, and one that stands out from your competition. Test both!</p>
          </div>
        `
      },
      {
        title: 'Metadata Split-Testing Framework',
        description: 'Systematic approach to testing and optimizing book metadata for higher rankings',
        type: 'framework',
        duration: '10 min',
        level: 'Advanced',
        content: `
          <h3>📊 The 30-Day Metadata Optimization System</h3>
          
          <h4>What to Test (In Order of Impact)</h4>
          
          <h5>Week 1: Title Variations</h5>
          <div class="bg-gray-800/50 rounded p-4 mb-4">
            <p><strong>Test A:</strong> Benefit-focused title</p>
            <p>Example: "Digital Marketing Mastery: Get 1000+ Customers Without Paid Ads"</p>
            
            <p><strong>Test B:</strong> Problem-focused title</p>
            <p>Example: "Stop Losing Customers: The Small Business Owner's Marketing Playbook"</p>
            
            <p><strong>Metric:</strong> Click-through rate from search results</p>
          </div>
          
          <h5>Week 2: Description Testing</h5>
          <div class="bg-gray-800/50 rounded p-4 mb-4">
            <p><strong>Test A:</strong> Story-driven opening</p>
            <p>"Sarah's restaurant was failing until she discovered..."</p>
            
            <p><strong>Test B:</strong> Stat-driven opening</p>  
            <p>"97% of small businesses fail because they ignore..."</p>
            
            <p><strong>Metric:</strong> Conversion rate (views to purchases)</p>
          </div>
          
          <h5>Week 3: Category Optimization</h5>
          <div class="bg-gray-800/50 rounded p-4 mb-4">
            <p><strong>Test A:</strong> Competitive category (high traffic)</p>
            <p><strong>Test B:</strong> Niche category (easier to rank)</p>
            
            <p><strong>Metric:</strong> Ranking position + total sales</p>
          </div>
          
          <h5>Week 4: Keyword Density</h5>
          <div class="bg-gray-800/50 rounded p-4 mb-4">
            <p><strong>Test A:</strong> High keyword density (5-7 main keywords)</p>
            <p><strong>Test B:</strong> Natural keyword integration (3-4 keywords)</p>
            
            <p><strong>Metric:</strong> Organic search ranking</p>
          </div>
          
          <h4>📈 Tracking & Analytics Setup</h4>
          
          <h5>Essential Metrics (Track Daily):</h5>
          <ol>
            <li><strong>BSR (Best Seller Rank):</strong> Lower = better</li>
            <li><strong>Page Views:</strong> From KDP dashboard</li>
            <li><strong>Conversion Rate:</strong> Views ÷ Sales</li>
            <li><strong>Keyword Rankings:</strong> Use Publisher Rocket or similar</li>
          </ol>
          
          <h5>The Test Results Matrix:</h5>
          <div class="bg-blue-500/20 border border-blue-500/30 rounded-lg p-4 mb-4">
            <table class="w-full text-sm">
              <tr>
                <td><strong>High Traffic + High Conversion:</strong></td>
                <td>Winner! Keep this variation</td>
              </tr>
              <tr>
                <td><strong>High Traffic + Low Conversion:</strong></td>
                <td>Good title/keywords, weak description</td>
              </tr>
              <tr>
                <td><strong>Low Traffic + High Conversion:</strong></td>
                <td>Great content, needs better keywords</td>
              </tr>
              <tr>
                <td><strong>Low Traffic + Low Conversion:</strong></td>
                <td>Start over with new approach</td>
              </tr>
            </table>
          </div>
          
          <h4>🔧 Implementation Tools</h4>
          
          <h5>Testing Schedule:</h5>
          <ul>
            <li><strong>Monday:</strong> Implement new variation</li>
            <li><strong>Tuesday-Thursday:</strong> Monitor initial data</li>
            <li><strong>Friday:</strong> Compile weekly results</li>
            <li><strong>Weekend:</strong> Plan next week's test</li>
          </ul>
          
          <h5>Quick Changes (Can test weekly):</h5>
          <ul>
            <li>Book description</li>
            <li>Keywords/tags</li>
            <li>Price point</li>
          </ul>
          
          <h5>Slow Changes (Test monthly):</h5>
          <ul>
            <li>Title changes</li>
            <li>Category changes</li>
            <li>Cover updates</li>
          </ul>
          
          <div class="bg-green-500/20 border border-green-500/30 rounded-lg p-4 mt-4">
            <h4>🚀 Pro Strategy</h4>
            <p>Run the same test on 2-3 books simultaneously. This gives you faster data and helps identify patterns that work across your entire catalog.</p>
          </div>
        `
      },
      {
        title: 'Genre Research & Market Analysis',
        description: 'Find profitable niches and validate market demand before writing',
        type: 'strategy',
        duration: '18 min',
        level: 'Intermediate',
        content: `
          <h3>🕵️ The Million-Dollar Question: What Should I Write About?</h3>
          
          <h4>The Goldilocks Principle</h4>
          <p>You want a niche that's:</p>
          <ul>
            <li><strong>Not too hot:</strong> Avoid oversaturated markets (weight loss, make money online)</li>
            <li><strong>Not too cold:</strong> Avoid markets with zero demand</li>
            <li><strong>Just right:</strong> Passionate audience + manageable competition</li>
          </ul>
          
          <h4>🔍 The 5-Step Research Process</h4>
          
          <h5>Step 1: Brainstorm Your Advantage Areas</h5>
          <div class="bg-purple-500/20 border border-purple-500/30 rounded-lg p-4 mb-4">
            <p><strong>Your Experience:</strong> What do you do for work? What are your hobbies?</p>
            <p><strong>Your Struggles:</strong> What problems have you solved in your life?</p>
            <p><strong>Your Interests:</strong> What do you research/read about in your free time?</p>
            <p><strong>Your Observations:</strong> What do you see people struggling with around you?</p>
          </div>
          
          <h5>Step 2: Amazon Research Deep-Dive</h5>
          <ol>
            <li><strong>Search your topic</strong> in Kindle books</li>
            <li><strong>Look for books with:</strong> 100-1000 reviews (sweet spot)</li>
            <li><strong>Check publication dates:</strong> Recent books getting traction = growing market</li>
            <li><strong>Read negative reviews:</strong> What are readers complaining about? (Your opportunity!)</li>
          </ol>
          
          <h5>Step 3: Competition Analysis</h5>
          <div class="bg-blue-500/20 border border-blue-500/30 rounded-lg p-4 mb-4">
            <h6>Green Light Indicators:</h6>
            <ul>
              <li>Top 10 books have &lt;500 reviews each</li>
              <li>Several books published in last 12 months</li>
              <li>Mix of traditional and indie publishers</li>
              <li>Price range: $2.99-$9.99 (profitable zone)</li>
            </ul>
            
            <h6>Red Flag Indicators:</h6>
            <ul>
              <li>Top books all have 5,000+ reviews</li>
              <li>Dominated by major publishers only</li>
              <li>All books under $0.99 (race to bottom)</li>
              <li>No new books in top 20 for 2+ years</li>
            </ul>
          </div>
          
          <h5>Step 4: Keyword Demand Validation</h5>
          <div class="bg-gray-800/50 rounded p-4 mb-4">
            <p><strong>Google Trends:</strong> Is interest growing or declining?</p>
            <p><strong>YouTube Search:</strong> Lots of recent videos = active interest</p>
            <p><strong>Reddit/Forums:</strong> Active communities asking questions?</p>
            <p><strong>Online Courses:</strong> Are people selling courses on this topic?</p>
          </div>
          
          <h5>Step 5: Profit Potential Calculator</h5>
          <div class="bg-green-500/20 border border-green-500/30 rounded-lg p-4 mb-4">
            <p><strong>Conservative Estimate:</strong> Rank #100,000 = 1 sale/day = $30/month</p>
            <p><strong>Moderate Success:</strong> Rank #50,000 = 3 sales/day = $90/month</p>
            <p><strong>Good Performance:</strong> Rank #20,000 = 10 sales/day = $300/month</p>
            <p><strong>Bestseller Territory:</strong> Rank #5,000 = 30+ sales/day = $900+/month</p>
          </div>
          
          <h4>🚀 High-Opportunity Niches (2024/2025)</h4>
          
          <h5>Emerging Hot Niches:</h5>
          <ul>
            <li><strong>AI/Automation for Small Business:</strong> Everyone wants to understand AI</li>
            <li><strong>Remote Work Optimization:</strong> Still growing post-pandemic</li>
            <li><strong>Sustainable Living:</strong> Eco-consciousness trending up</li>
            <li><strong>Mental Health for Entrepreneurs:</strong> Underserved but growing</li>
            <li><strong>Senior Tech Help:</strong> Boomers need simple tech guides</li>
          </ul>
          
          <h5>Evergreen Profitable Niches:</h5>
          <ul>
            <li><strong>Specific Diet Plans:</strong> Not "weight loss" but "keto for women over 40"</li>
            <li><strong>Hobby Monetization:</strong> "Turn your [HOBBY] into income"</li>
            <li><strong>Relationship Sub-niches:</strong> Dating after divorce, long-distance relationships</li>
            <li><strong>Parenting Solutions:</strong> Specific age ranges and problems</li>
          </ul>
          
          <h4>📋 Research Checklist Template</h4>
          <div class="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4">
            <h6>Before you write, verify:</h6>
            <ul>
              <li>✅ 5+ books in top 100 with &lt;1000 reviews</li>
              <li>✅ Growing search trend (Google Trends)</li>
              <li>✅ Active communities discussing topic</li>
              <li>✅ Price point allows for profit ($2.99+)</li>
              <li>✅ You can write 10+ books in this niche</li>
            </ul>
          </div>
          
          <div class="bg-red-500/20 border border-red-500/30 rounded-lg p-4 mt-4">
            <h4>⚠️ Warning</h4>
            <p>Don't skip research! 2 hours of research can save you 20 hours of writing a book nobody wants.</p>
          </div>
        `
      }
    ],
    advanced: [
      {
        title: 'Series Planning for Maximum Revenue',
        description: 'Strategic framework for building profitable book series that generate recurring income',
        type: 'strategy',
        duration: '20 min',
        level: 'Expert',
        content: `
          <h3>📈 The Series Goldmine: Why Series Outsell Single Books 5:1</h3>
          
          <h4>The Series Psychology</h4>
          <p>Readers who finish Book 1 have a 73% chance of buying Book 2, and 89% chance of buying Book 3. This is why Amazon promotes series heavily.</p>
          
          <h4>📊 Series Revenue Models</h4>
          
          <h5>The Hook & Expand Model:</h5>
          <div class="bg-green-500/20 border border-green-500/30 rounded-lg p-4 mb-4">
            <p><strong>Book 1:</strong> Lower price ($0.99-$2.99) - Customer acquisition</p>
            <p><strong>Books 2-5:</strong> Premium price ($4.99-$9.99) - Profit maximization</p>
            <p><strong>Example:</strong> Book 1 at $0.99 brings in reader, Books 2-4 at $5.99 = $18.96 total revenue vs $5.99 single book</p>
          </div>
          
          <h5>The Authority Series Model:</h5>
          <div class="bg-blue-500/20 border border-blue-500/30 rounded-lg p-4 mb-4">
            <p><strong>All Books:</strong> Premium price ($7.99-$12.99)</p>
            <p><strong>Target:</strong> Professional audience willing to pay for expertise</p>
            <p><strong>Best For:</strong> Business, finance, specialized skills</p>
          </div>
          
          <h4>🔄 The 5-Book Series Framework</h4>
          
          <h5>Book Structure Template:</h5>
          <ol>
            <li><strong>Book 1 - Foundation:</strong> "Getting Started with [TOPIC]"</li>
            <li><strong>Book 2 - Implementation:</strong> "Advanced [TOPIC] Strategies"</li>
            <li><strong>Book 3 - Specialization:</strong> "[TOPIC] for [SPECIFIC SITUATION]"</li>
            <li><strong>Book 4 - Mastery:</strong> "Expert [TOPIC] Techniques"</li>
            <li><strong>Book 5 - Innovation:</strong> "The Future of [TOPIC]"</li>
          </ol>
          
          <h5>Content Progression Strategy:</h5>
          <ul>
            <li><strong>25% New Content:</strong> Each book builds on previous</li>
            <li><strong>15% Recap Content:</strong> Reinforce key concepts</li>
            <li><strong>60% Advanced Content:</strong> New strategies and techniques</li>
          </ul>
          
          <h4>💰 Revenue Optimization Tactics</h4>
          
          <h5>Cross-Promotion Inside Books:</h5>
          <div class="bg-purple-500/20 border border-purple-500/30 rounded-lg p-4 mb-4">
            <p><strong>Book 1 End:</strong> "In Book 2, you'll discover the advanced strategies that helped Sarah increase her revenue by 300%..."</p>
            <p><strong>Include:</strong> Direct Amazon links to next book</p>
            <p><strong>Bonus:</strong> Exclusive content for series readers</p>
          </div>
          
          <h5>Email List Building:</h5>
          <ul>
            <li><strong>Lead Magnet:</strong> "Complete [SERIES TOPIC] Resource Kit"</li>
            <li><strong>Collect in Book 1:</strong> Offer bonus materials for email signup</li>
            <li><strong>Nurture Sequence:</strong> Email series promoting Books 2-5</li>
          </ul>
          
          <div class="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4 mt-4">
            <h4>Success Metric</h4>
            <p>A well-executed 5-book series can generate $50,000-$200,000 annually. Even conservative estimates show 10x ROI over single books.</p>
          </div>
        `
      },
      {
        title: 'Advanced Keyword Stacking Techniques',
        description: 'Layer keywords strategically to dominate Amazon search results across multiple terms',
        type: 'technique',
        duration: '15 min',
        level: 'Advanced',
        content: `
          <h3>🔍 The Keyword Domination System: Own Multiple Search Terms</h3>
          
          <h4>Understanding Keyword Layers</h4>
          <p>Amazon's algorithm rewards books that rank for multiple related keywords. The goal is to create "keyword clusters" where you dominate an entire topic area.</p>
          
          <h4>📊 The 3-Layer Keyword Strategy</h4>
          
          <h5>Layer 1: Primary Keywords (Title)</h5>
          <div class="bg-red-500/20 border border-red-500/30 rounded-lg p-4 mb-4">
            <p><strong>Target:</strong> 1-2 high-volume keywords</p>
            <p><strong>Example:</strong> "Digital Marketing" + "Small Business"</p>
            <p><strong>Placement:</strong> Early in title for maximum weight</p>
          </div>
          
          <h5>Layer 2: Secondary Keywords (Subtitle)</h5>
          <div class="bg-blue-500/20 border border-blue-500/30 rounded-lg p-4 mb-4">
            <p><strong>Target:</strong> 3-4 medium-volume keywords</p>
            <p><strong>Example:</strong> "Social Media Marketing", "Content Strategy", "Lead Generation"</p>
            <p><strong>Placement:</strong> Subtitle and first paragraph of description</p>
          </div>
          
          <h5>Layer 3: Long-tail Keywords (Description + Tags)</h5>
          <div class="bg-green-500/20 border border-green-500/30 rounded-lg p-4 mb-4">
            <p><strong>Target:</strong> 8-10 specific long-tail phrases</p>
            <p><strong>Example:</strong> "digital marketing for restaurants", "social media automation tools"</p>
            <p><strong>Placement:</strong> Throughout description and in keyword tags</p>
          </div>
          
          <h4>🔗 Semantic Keyword Clustering</h4>
          
          <h5>The Cluster Method:</h5>
          <ol>
            <li><strong>Core Topic:</strong> Start with main keyword</li>
            <li><strong>Related Terms:</strong> Find 15-20 related keywords</li>
            <li><strong>Group by Intent:</strong> Organize by what searcher wants</li>
            <li><strong>Natural Integration:</strong> Weave into content naturally</li>
          </ol>
          
          <h5>Example Cluster - "Digital Marketing":</h5>
          <div class="bg-gray-800/50 rounded p-4 mb-4">
            <p><strong>Learning Intent:</strong> "digital marketing guide", "how to learn digital marketing"</p>
            <p><strong>Business Intent:</strong> "digital marketing for small business", "digital marketing strategies"</p>
            <p><strong>Tool Intent:</strong> "digital marketing tools", "digital marketing software"</p>
            <p><strong>Service Intent:</strong> "digital marketing services", "digital marketing agency"</p>
          </div>
          
          <h4>Advanced Stacking Techniques</h4>
          
          <h5>1. The Trojan Horse Method</h5>
          <p>Rank for a less competitive keyword first, then gradually optimize for more competitive terms:</p>
          <ul>
            <li><strong>Month 1:</strong> Target "email marketing for restaurants" (low competition)</li>
            <li><strong>Month 2:</strong> Add "restaurant marketing" to description</li>
            <li><strong>Month 3:</strong> Optimize for "digital marketing" (high competition)</li>
          </ul>
          
          <h5>2. The Cross-Pollination Strategy</h5>
          <p>Use keywords from related but different categories:</p>
          <ul>
            <li><strong>Primary Category:</strong> Business Marketing</li>
            <li><strong>Secondary Keywords:</strong> From Entrepreneurship, Small Business, Online Business</li>
            <li><strong>Result:</strong> Appear in multiple category searches</li>
          </ul>
          
          <h5>3. The Seasonal Stack</h5>
          <p>Layer in seasonal keywords for periodic ranking boosts:</p>
          <ul>
            <li><strong>Q4:</strong> "holiday marketing", "Christmas marketing"</li>
            <li><strong>Q1:</strong> "new year business goals", "marketing resolutions"</li>
            <li><strong>Summer:</strong> "summer marketing campaigns"</li>
          </ul>
          
          <h4>📈 Measuring Keyword Success</h4>
          
          <h5>Key Metrics to Track:</h5>
          <ul>
            <li><strong>Keyword Rankings:</strong> Position for each target keyword</li>
            <li><strong>Organic Traffic:</strong> Visitors from non-advertising sources</li>
            <li><strong>Conversion Rate:</strong> Keywords that actually drive sales</li>
            <li><strong>Ranking Velocity:</strong> How quickly you move up for new keywords</li>
          </ul>
          
          <div class="bg-purple-500/20 border border-purple-500/30 rounded-lg p-4 mt-4">
            <h4>Pro Tip</h4>
            <p>The best keyword stackers rank for 50+ keywords per book. Start with 10 keywords and add 5 new ones each month through description updates.</p>
          </div>
        `
      }
    ],
    workflows: [
      {
        title: 'Automated Book Creation Pipeline',
        description: 'Set up systems for consistent publishing without burning out',
        type: 'system',
        duration: '25 min',
        level: 'Intermediate',
        content: `
          <h3>🏭 The Book Factory: Systematic Publishing for Scale</h3>
          
          <h4>The 30-Day Publishing Cycle</h4>
          <p>Consistency beats perfection. Here's how to publish 1 book every 30 days without stress:</p>
          
          <h4>📅 Week-by-Week Breakdown</h4>
          
          <h5>Week 1: Research & Planning</h5>
          <ul>
            <li><strong>Day 1-2:</strong> Market research and niche validation</li>
            <li><strong>Day 3-4:</strong> Outline creation and chapter planning</li>
            <li><strong>Day 5-7:</strong> Content research and source material gathering</li>
          </ul>
          
          <h5>Week 2: Content Creation</h5>
          <ul>
            <li><strong>Day 8-10:</strong> Generate first draft with AI BookMoney (8-12 credits)</li>
            <li><strong>Day 11-12:</strong> First review and major edits</li>
            <li><strong>Day 13-14:</strong> Second review and polish</li>
          </ul>
          
          <h5>Week 3: Design & Optimization</h5>
          <ul>
            <li><strong>Day 15-16:</strong> Cover generation and testing (3-5 credits)</li>
            <li><strong>Day 17-18:</strong> Metadata optimization and keyword research (2-3 credits)</li>
            <li><strong>Day 19-21:</strong> Format for multiple platforms (PDF, EPUB, MOBI)</li>
          </ul>
          
          <h5>Week 4: Launch & Promotion</h5>
          <ul>
            <li><strong>Day 22-24:</strong> KDP upload and listing optimization</li>
            <li><strong>Day 25-27:</strong> Marketing material creation</li>
            <li><strong>Day 28-30:</strong> Launch sequence and promotion</li>
          </ul>
          
          <h4>⚙️ Automation Tools & Templates</h4>
          
          <h5>Content Creation Templates:</h5>
          <div class="bg-blue-500/20 border border-blue-500/30 rounded-lg p-4 mb-4">
            <p><strong>Chapter Template:</strong> Problem → Solution → Example → Action Steps</p>
            <p><strong>Introduction Template:</strong> Hook → Promise → Proof → Preview</p>
            <p><strong>Conclusion Template:</strong> Summary → Next Steps → Call to Action</p>
          </div>
          
          <h5>AI Prompt Templates (Save for Reuse):</h5>
          <div class="bg-gray-800/50 rounded p-4 mb-4">
            <pre>BOOK INTRO PROMPT:
"Write an engaging introduction for a [GENRE] book titled '[TITLE]'. 
Target audience: [AUDIENCE]
Main benefit: [BENEFIT]
Include: Hook story, credibility markers, chapter preview
Tone: [TONE]
Length: 500-700 words"</pre>
          </div>
          
          <h4>📊 Quality Control Checklist</h4>
          
          <h5>Before Every Upload:</h5>
          <ul>
            <li>✅ Spell check and grammar review completed</li>
            <li>✅ All chapters follow consistent structure</li>
            <li>✅ Cover passes thumbnail readability test</li>
            <li>✅ Metadata includes 7+ relevant keywords</li>
            <li>✅ Book description has compelling hook</li>
            <li>✅ All formats tested and working</li>
            <li>✅ Competition analysis completed</li>
          </ul>
          
          <h4>🔄 Batch Processing Strategies</h4>
          
          <h5>Content Batching:</h5>
          <ul>
            <li><strong>Research Day:</strong> Research 3-4 topics in one session</li>
            <li><strong>Writing Day:</strong> Generate 2-3 book drafts using AI</li>
            <li><strong>Design Day:</strong> Create 5-6 covers for different books</li>
            <li><strong>Upload Day:</strong> Process and upload multiple books</li>
          </ul>
          
          <h5>Credit Optimization:</h5>
          <div class="bg-green-500/20 border border-green-500/30 rounded-lg p-4 mb-4">
            <p><strong>Monthly Credit Budget:</strong> Plan for 4 books = ~50 credits</p>
            <p><strong>Book 1:</strong> 12 credits (premium length)</p>
            <p><strong>Books 2-3:</strong> 10 credits each (standard)</p>
            <p><strong>Book 4:</strong> 8 credits (shorter, testing new niche)</p>
            <p><strong>Extra Credits:</strong> Covers and metadata optimization</p>
          </div>
          
          <div class="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4 mt-4">
            <h4>Success Metric</h4>
            <p>Month 1: 1 book. Month 6: 6 books generating passive income. Month 12: 12+ books with optimized funnel system.</p>
          </div>
        `
      }
    ],
    troubleshooting: [
      {
        title: 'Common KDP Upload Issues',
        description: 'Fix the 5 most common formatting and upload problems that reject 80% of first-time uploads',
        type: 'guide',
        duration: '8 min',
        level: 'Beginner',
        content: `
          <h3>🚫 The 5 KDP Upload Killers (And How to Fix Them)</h3>
          
          <h4>❌ Issue #1: Wrong File Format</h4>
          <div class="bg-red-500/20 border border-red-500/30 rounded-lg p-4 mb-4">
            <p><strong>Problem:</strong> Uploading .docx instead of PDF, or corrupted files</p>
            <p><strong>Solution:</strong> Use AI BookMoney's "Download All Formats" - we generate KDP-optimized files</p>
            <p><strong>Best Practice:</strong> Always test your PDF before uploading (zoom to 150% to check formatting)</p>
          </div>
          
          <h4>❌ Issue #2: Cover Dimension Problems</h4>
          <div class="bg-red-500/20 border border-red-500/30 rounded-lg p-4 mb-4">
            <p><strong>Problem:</strong> Cover doesn't meet KDP's exact specifications</p>
            <p><strong>Requirements:</strong> Minimum 2560x1600 pixels, ideal ratio 1.6:1</p>
            <p><strong>Solution:</strong> Our Cover Generator uses "KDP Mode" - guaranteed compliant dimensions</p>
          </div>
          
          <h4>❌ Issue #3: Copyright Issues</h4>
          <div class="bg-red-500/20 border border-red-500/30 rounded-lg p-4 mb-4">
            <p><strong>Problem:</strong> Using copyrighted images or content without permission</p>
            <p><strong>Solution:</strong> All AI BookMoney generated content and covers are copyright-safe</p>
            <p><strong>Warning Signs:</strong> KDP flags books with similar titles to existing works</p>
          </div>
          
          <h4>❌ Issue #4: Metadata Violations</h4>
          <div class="bg-red-500/20 border border-red-500/30 rounded-lg p-4 mb-4">
            <p><strong>Problem:</strong> Keywords that violate KDP terms or misleading descriptions</p>
            <p><strong>Forbidden:</strong> "Bestseller", competitor names, misleading claims</p>
            <p><strong>Solution:</strong> Our Metadata Optimizer automatically filters prohibited terms</p>
          </div>
          
          <h4>❌ Issue #5: Quality Threshold Failures</h4>
          <div class="bg-red-500/20 border border-red-500/30 rounded-lg p-4 mb-4">
            <p><strong>Problem:</strong> KDP's quality review flags low-quality content</p>
            <p><strong>Common Causes:</strong> Too short (&lt;24 pages), repetitive content, poor formatting</p>
            <p><strong>Solution:</strong> Our books meet KDP's quality standards with proper structure and length</p>
          </div>
          
          <h4>✅ Pre-Upload Checklist</h4>
          <ol>
            <li>📄 File is PDF format and under 50MB</li>
            <li>🖼️ Cover is 2560x1600+ pixels</li>
            <li>📝 Description under 4000 characters</li>
            <li>🏷️ No prohibited keywords used</li>
            <li>📊 Book is 24+ pages with proper formatting</li>
            <li>🔍 No copyright violations in content or images</li>
            <li>💰 Price set appropriately for length and genre</li>
          </ol>
          
          <div class="bg-green-500/20 border border-green-500/30 rounded-lg p-4 mt-4">
            <h4>Pro Tip</h4>
            <p>AI BookMoney handles all technical requirements automatically. Our "KDP Ready" download package includes everything formatted correctly for upload.</p>
          </div>
        `
      },
      {
        title: 'Cover Generation Troubleshooting',
        description: 'Resolve common cover creation issues and optimize for conversions',
        type: 'guide',
        duration: '6 min',
        level: 'Beginner',
        content: `
          <h3>🎨 Cover Generation Quick Fixes</h3>
          
          <h4>❌ Issue #1: Text Too Small to Read</h4>
          <div class="bg-red-500/20 border border-red-500/30 rounded-lg p-4 mb-4">
            <p><strong>Problem:</strong> Title disappears when viewed as thumbnail</p>
            <p><strong>Solution:</strong> Use shorter titles (5-7 words max) and select "Large Text" option</p>
            <p><strong>Test:</strong> Shrink cover to 150x240 pixels - can you still read the title?</p>
          </div>
          
          <h4>❌ Issue #2: Cover Looks Generic</h4>
          <div class="bg-red-500/20 border border-red-500/30 rounded-lg p-4 mb-4">
            <p><strong>Problem:</strong> Cover blends in with thousands of others</p>
            <p><strong>Solution:</strong> Use contrasting colors for your genre, add unique visual elements</p>
            <p><strong>Tip:</strong> Check top 20 books in your category - avoid similar color schemes</p>
          </div>
          
          <h4>❌ Issue #3: Wrong Genre Signals</h4>
          <div class="bg-red-500/20 border border-red-500/30 rounded-lg p-4 mb-4">
            <p><strong>Problem:</strong> Cover doesn't match reader expectations</p>
            <p><strong>Romance:</strong> Use warm colors, flowing fonts, people/couples</p>
            <p><strong>Business:</strong> Clean, professional, charts/graphs, cool colors</p>
            <p><strong>Thriller:</strong> Dark colors, bold fonts, mysterious imagery</p>
          </div>
          
          <h4>✅ Cover Generation Best Practices</h4>
          <ul>
            <li>• Generate 3-5 variations and A/B test</li>
            <li>• Always check mobile thumbnail readability</li>
            <li>• Use high contrast for title text</li>
            <li>• Study bestsellers in your exact sub-genre</li>
            <li>• Ask for feedback in author groups before publishing</li>
          </ul>
          
          <div class="bg-green-500/20 border border-green-500/30 rounded-lg p-4 mt-4">
            <h4>Pro Tip</h4>
            <p>Great covers sell books, but terrible covers kill them. Spend the extra credits to get it right - a cover can make or break your book's success.</p>
          </div>
        `
      },
      {
        title: 'Credit & Billing FAQ',
        description: 'Common questions about credit usage, billing, and account management',
        type: 'faq',
        duration: '5 min',
        level: 'Beginner',
        content: `
          <h3>💰 Credits & Billing: Your Questions Answered</h3>
          
          <h4>❓ How do credits work?</h4>
          <div class="bg-blue-500/20 border border-blue-500/30 rounded-lg p-4 mb-4">
            <p><strong>Answer:</strong> Credits are consumed when you generate content:</p>
            <ul>
              <li><strong>Book Generation:</strong> 8-12 credits (based on length)</li>
              <li><strong>Cover Creation:</strong> 3-5 credits per cover</li>
              <li><strong>Metadata Optimization:</strong> 2-3 credits per book</li>
            </ul>
          </div>
          
          <h4>❓ What happens when I run out of credits?</h4>
          <div class="bg-blue-500/20 border border-blue-500/30 rounded-lg p-4 mb-4">
            <p><strong>Answer:</strong> You can purchase credit top-up packages anytime:</p>
            <ul>
              <li><strong>Quick Boost:</strong> 50 credits for $19.99</li>
              <li><strong>Creator Pack:</strong> 150 credits for $49.99</li>
              <li><strong>Pro Bundle:</strong> 400 credits for $99.99</li>
              <li><strong>Enterprise:</strong> 1000 credits for $199.99</li>
            </ul>
          </div>
          
          <h4>❓ Do credits expire?</h4>
          <div class="bg-green-500/20 border border-green-500/30 rounded-lg p-4 mb-4">
            <p><strong>Answer:</strong> No! Credits never expire as long as you maintain an active subscription.</p>
          </div>
          
          <h4>❓ Can I get refunds for unused credits?</h4>
          <div class="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4 mb-4">
            <p><strong>Answer:</strong> Credits are non-refundable, but they never expire. We recommend starting with smaller packs to test the platform.</p>
          </div>
          
          <h4>❓ Why does regeneration cost the same as original generation?</h4>
          <div class="bg-blue-500/20 border border-blue-500/30 rounded-lg p-4 mb-4">
            <p><strong>Answer:</strong> Each generation uses the same AI processing power. To minimize regeneration:</p>
            <ul>
              <li>Spend time crafting detailed, specific prompts</li>
              <li>Use our prompt templates and examples</li>
              <li>Review generated content before regenerating</li>
            </ul>
          </div>
          
          <h4>❓ How can I track my credit usage?</h4>
          <div class="bg-blue-500/20 border border-blue-500/30 rounded-lg p-4 mb-4">
            <p><strong>Answer:</strong> Check your dashboard for:</p>
            <ul>
              <li>Current credit balance (top right corner)</li>
              <li>Credit usage history (Credit Purchase page)</li>
              <li>Recommended top-up packages based on your usage</li>
            </ul>
          </div>
          
          <div class="bg-purple-500/20 border border-purple-500/30 rounded-lg p-4 mt-4">
            <h4>Credit Optimization Tip</h4>
            <p>Most successful authors use 30-50 credits per month. Plan your book pipeline to maximize your investment!</p>
          </div>
        `
      }
    ]
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'video': return <Video className="h-4 w-4" />;
      case 'interactive': return <Zap className="h-4 w-4" />;
      case 'masterclass': return <Star className="h-4 w-4" />;
      case 'tutorial': return <PlayCircle className="h-4 w-4" />;
      case 'article': return <FileText className="h-4 w-4" />;
      case 'checklist': return <CheckCircle className="h-4 w-4" />;
      case 'faq': return <HelpCircle className="h-4 w-4" />;
      default: return <Book className="h-4 w-4" />;
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'Beginner': return 'bg-green-500/20 text-green-300';
      case 'Intermediate': return 'bg-blue-500/20 text-blue-300';
      case 'Advanced': return 'bg-purple-500/20 text-purple-300';
      case 'Expert': return 'bg-red-500/20 text-red-300';
      default: return 'bg-gray-500/20 text-gray-300';
    }
  };



  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Pro Publisher Vault
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Advanced strategies, tutorials, and workflows exclusively for KDP Publishing Pro members
          </p>
        </div>

        {/* Welcome Card */}
        <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-2xl p-6 mb-8 border border-purple-500/30">
          <div className="flex items-center space-x-4">
            <div className="bg-purple-600 rounded-full p-3">
              <User className="h-8 w-8 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-white">Welcome back, {user?.full_name}!</h2>
              <p className="text-purple-200">Continue your publishing journey with exclusive content</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-white">{user?.credits || 0}</div>
              <p className="text-purple-200 text-sm">Credits Available</p>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search tutorials, guides, and strategies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-800/50 border border-purple-500/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-gray-900/50 rounded-2xl p-6 border border-purple-500/20 sticky top-24">
              <h3 className="text-lg font-bold text-white mb-4">Content Sections</h3>
              <nav className="space-y-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => handleSectionChange(section.id)}
                    className={`w-full text-left p-3 rounded-lg transition-colors flex items-center space-x-3 ${
                      activeSection === section.id
                        ? 'bg-purple-600 text-white'
                        : 'text-gray-300 hover:text-white hover:bg-purple-500/20'
                    }`}
                  >
                    {section.icon}
                    <div className="flex-1">
                      <div className="font-medium">{section.title}</div>
                      <div className="text-xs opacity-75">{section.description}</div>
                    </div>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-gray-900/50 rounded-2xl p-6 border border-purple-500/20">
              <div className="mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    {sections.find(s => s.id === activeSection)?.title}
                  </h2>
                  <p className="text-gray-300">
                    {sections.find(s => s.id === activeSection)?.description}
                  </p>
                </div>
              </div>

              {/* Content Grid */}
              {!selectedTutorial && activeSection !== 'contact' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {tutorialContent[activeSection]?.map((item, index) => (
                    <div 
                      key={index} 
                      onClick={() => setSelectedTutorial(item)}
                      className="bg-gray-800/50 rounded-lg p-6 border border-purple-500/10 hover:border-purple-500/30 transition-all group cursor-pointer"
                    >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <div className="bg-purple-600/20 rounded-lg p-2 text-purple-400">
                          {getTypeIcon(item.type)}
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getLevelColor(item.level)}`}>
                          {item.level}
                        </span>
                      </div>
                      <div className="text-gray-400 text-sm flex items-center space-x-1">
                        <Clock className="h-4 w-4" />
                        <span>{item.duration}</span>
                      </div>
                    </div>
                    
                    <h3 className="text-lg font-bold text-white mb-2 group-hover:text-purple-300 transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-gray-300 mb-4 text-sm">
                      {item.description}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-purple-400 text-sm font-medium capitalize">
                        {item.type}
                      </span>
                      <ArrowRight className="h-4 w-4 text-purple-400 group-hover:text-purple-300 transition-colors" />
                    </div>
                  </div>
                ))}
              </div>
              )}

              {/* Contact Support Form */}
              {activeSection === 'contact' && !selectedTutorial && (
                <div className="max-w-4xl">
                  {/* Quick Support Options */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    <div className="bg-gradient-to-br from-blue-600/20 to-blue-800/20 rounded-lg p-6 border border-blue-500/30">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="bg-blue-500/20 rounded-lg p-2">
                          <Mail className="h-5 w-5 text-blue-400" />
                        </div>
                        <h3 className="text-white font-semibold">Email Support</h3>
                      </div>
                      <p className="text-gray-300 text-sm mb-3">Get detailed help via email</p>
                      <p className="text-blue-400 text-sm">Response within 4-8 hours</p>
                    </div>
                    
                    <div className="bg-gradient-to-br from-green-600/20 to-green-800/20 rounded-lg p-6 border border-green-500/30">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="bg-green-500/20 rounded-lg p-2">
                          <Book className="h-5 w-5 text-green-400" />
                        </div>
                        <h3 className="text-white font-semibold">Documentation</h3>
                      </div>
                      <p className="text-gray-300 text-sm mb-3">Browse our help guides</p>
                      <Link to="/kdp-guide" className="text-green-400 text-sm hover:text-green-300">
                        View KDP Guide →
                      </Link>
                    </div>
                    
                    <div className="bg-gradient-to-br from-purple-600/20 to-purple-800/20 rounded-lg p-6 border border-purple-500/30">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="bg-purple-500/20 rounded-lg p-2">
                          <MessageSquare className="h-5 w-5 text-purple-400" />
                        </div>
                        <h3 className="text-white font-semibold">Priority Support</h3>
                      </div>
                      <p className="text-gray-300 text-sm mb-3">Premium members get priority</p>
                      <p className="text-purple-400 text-sm">
                        {user?.is_premium ? 'You have priority support' : 'Upgrade for priority'}
                      </p>
                    </div>
                  </div>

                  {/* Contact Form */}
                  <div className="bg-gray-800/50 rounded-lg p-6 border border-purple-500/10">
                    <div className="flex items-center space-x-3 mb-6">
                      <div className="bg-purple-500/20 rounded-lg p-2">
                        <Send className="h-5 w-5 text-purple-400" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Contact Our Support Team</h3>
                        <p className="text-gray-400">We'll get back to you as soon as possible</p>
                      </div>
                    </div>

                    <form onSubmit={handleContactSubmit} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Full Name
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={contactForm.name}
                            onChange={handleContactInputChange}
                            required
                            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            placeholder="Your full name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Email Address
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={contactForm.email}
                            onChange={handleContactInputChange}
                            required
                            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            placeholder="your.email@example.com"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Category
                          </label>
                          <select
                            name="category"
                            value={contactForm.category}
                            onChange={handleContactInputChange}
                            required
                            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          >
                            <option value="">Select a category</option>
                            <option value="technical">Technical Issue</option>
                            <option value="billing">Billing & Payments</option>
                            <option value="account">Account & Login</option>
                            <option value="features">Feature Request</option>
                            <option value="bug">Bug Report</option>
                            <option value="general">General Question</option>
                            <option value="feedback">Feedback</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Priority
                          </label>
                          <select
                            name="priority"
                            value={contactForm.priority}
                            onChange={handleContactInputChange}
                            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="urgent">Urgent</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Subject
                        </label>
                        <input
                          type="text"
                          name="subject"
                          value={contactForm.subject}
                          onChange={handleContactInputChange}
                          required
                          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="Brief description of your issue"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Message
                        </label>
                        <textarea
                          name="message"
                          value={contactForm.message}
                          onChange={handleContactInputChange}
                          required
                          rows={6}
                          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-vertical"
                          placeholder="Please describe your issue or question in detail. Include any error messages, steps you've taken, or relevant information that might help us assist you better."
                        />
                      </div>

                      {/* Account Context Display */}
                      {user && (
                        <div className="bg-gray-700/50 rounded-lg p-4 border border-gray-600/50">
                          <h4 className="text-sm font-medium text-gray-300 mb-2">Account Information (auto-included)</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-gray-400">Status:</span>
                              <span className={`ml-2 px-2 py-1 rounded text-xs ${user.is_premium ? 'bg-green-600/20 text-green-400' : 'bg-yellow-600/20 text-yellow-400'}`}>
                                {user.is_premium ? 'Premium' : 'Trial'}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-400">Credits:</span>
                              <span className="text-white ml-2">{user.credits || 0}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">Books:</span>
                              <span className="text-white ml-2">{user.books_generated || 0}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">Member since:</span>
                              <span className="text-white ml-2">
                                {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="flex justify-between items-center pt-4">
                        <div className="flex items-center space-x-2 text-sm text-gray-400">
                          <CheckCircle className="h-4 w-4 text-green-400" />
                          <span>We'll respond within 4-8 hours</span>
                        </div>
                        
                        <button
                          type="submit"
                          disabled={isSubmittingContact}
                          className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                        >
                          {isSubmittingContact ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              <span>Sending...</span>
                            </>
                          ) : (
                            <>
                              <Send className="h-4 w-4" />
                              <span>Send Message</span>
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Tutorial Detail View */}
              {selectedTutorial && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <button
                      onClick={() => setSelectedTutorial(null)}
                      className="flex items-center space-x-2 text-purple-400 hover:text-purple-300 transition-colors"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      <span>Back to {sections.find(s => s.id === activeSection)?.title}</span>
                    </button>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <div className="bg-purple-600/20 rounded-lg p-2 text-purple-400">
                          {getTypeIcon(selectedTutorial.type)}
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getLevelColor(selectedTutorial.level)}`}>
                          {selectedTutorial.level}
                        </span>
                      </div>
                      <div className="text-gray-400 text-sm flex items-center space-x-1">
                        <Clock className="h-4 w-4" />
                        <span>{selectedTutorial.duration}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-white mb-4">{selectedTutorial.title}</h1>
                    <p className="text-xl text-gray-300">{selectedTutorial.description}</p>
                  </div>
                  
                  <div 
                    className="help-content-display prose prose-invert max-w-none"
                    dangerouslySetInnerHTML={{ __html: selectedTutorial.content }}
                    style={{
                      color: '#e5e7eb',
                      lineHeight: '1.7'
                    }}
                  />
                </div>
              )}

              {/* Quick Actions */}
              {!selectedTutorial && (
              <div className="mt-8 pt-6 border-t border-purple-500/20">
                <h3 className="text-lg font-bold text-white mb-4">Quick Actions</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Link
                    to="/create"
                    className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg p-4 text-center hover:from-purple-700 hover:to-pink-700 transition-colors"
                  >
                    <BookOpen className="h-8 w-8 text-white mx-auto mb-2" />
                    <div className="text-white font-medium">Create New Book</div>
                  </Link>
                  <Link
                    to="/kdp-covers"
                    className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-4 text-center hover:from-blue-700 hover:to-purple-700 transition-colors"
                  >
                    <Palette className="h-8 w-8 text-white mx-auto mb-2" />
                    <div className="text-white font-medium">Generate Cover</div>
                  </Link>
                  <Link
                    to="/metadata-optimizer"
                    className="bg-gradient-to-r from-green-600 to-blue-600 rounded-lg p-4 text-center hover:from-green-700 hover:to-blue-700 transition-colors"
                  >
                    <Target className="h-8 w-8 text-white mx-auto mb-2" />
                    <div className="text-white font-medium">Optimize Metadata</div>
                  </Link>
                </div>
              </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpCenterPage;